import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import {
  Button,
  ButtonLink,
  EmptyStateBase,
  useModals,
  ButtonIcon,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { GoalDetailsContent } from "@/pages/talent/goals/details/components/GoalDetailsContent.tsx";
import { GoalCheckInEditModal } from "@/pages/talent/goals/widgets/GoalCheckInEditModal.tsx";
import { GoalEditModal } from "@/pages/talent/goals/widgets/GoalEditModal.tsx";
import { useGoal } from "@/queries/goals.query.ts";
import { useTranslation } from "react-i18next";

interface GoalDetailPanelProps {
  goalId: number;
  onClose: () => void;
}

export function GoalDetailPanel({ goalId, onClose }: GoalDetailPanelProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const { data: goal, isLoading } = useGoal(goalId, !!goalId);

  const handleEdit = () => {
    if (goalId) {
      openModal(GoalEditModal, { goalId });
    }
  };

  const handleUpdateProgress = () => {
    if (goalId) {
      openModal(GoalCheckInEditModal, { goalId });
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/goals/${goalId}`;
    copyTextWithNotify(url);
  };

  if (isLoading) {
    return (
      <Stack className="h-full">
        <Stack
          direction="horizontal"
          items="center"
          justify="between"
          className="px-4 py-3 border-b border-dark/5"
        >
          <Text className="font-semibold">{t("goals:goal")}</Text>
          <Stack direction="horizontal" gap="xs">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </Stack>
        </Stack>
        <Stack className="p-4" gap="md">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-20 w-full" />
        </Stack>
      </Stack>
    );
  }

  if (!goal) {
    return (
      <Stack className="h-full">
        <Stack
          direction="horizontal"
          items="center"
          justify="between"
          className="px-4 py-3 border-b border-dark/5"
        >
          <Text className="font-semibold">{t("goals:goal")}</Text>
          <ButtonIcon
            icon={<FontAwesomeIcon icon="fa-light fa-xmark" />}
            variant="plain"
            onClick={onClose}
          />
        </Stack>
        <Stack className="p-4 items-center justify-center flex-1">
          <EmptyStateBase
            emoji={":mag_right:"}
            title={t("goals:goal_not_found")}
            description={t("goals:goal_not_found_description")}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack className="h-full">
      <Stack
        direction="horizontal"
        items="center"
        justify="between"
        className="px-4 py-3 border-b border-dark/5 shrink-0"
      >
        <Text className="font-semibold">{t("goals:goal")}</Text>
        <Stack direction="horizontal" gap="sm">
          {goal?.acl.can_edit && (
            <Button variant="secondary" onClick={handleUpdateProgress}>
              {t("common:update_progress")}
            </Button>
          )}
          {goal?.acl.can_edit && (
            <Tooltip>
              <TooltipTrigger>
                <ButtonIcon
                  icon={<FontAwesomeIcon icon="fa-light fa-pen" />}
                  variant="secondary"
                  onClick={handleEdit}
                />
              </TooltipTrigger>
              <TooltipContent>{t("common:edit")}</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger>
              <ButtonIcon
                icon={<FontAwesomeIcon icon="fa-light fa-link" />}
                variant="secondary"
                onClick={handleCopyLink}
              />
            </TooltipTrigger>
            <TooltipContent>{t("common:copy_link")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger>
              <ButtonLink
                leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />}
                variant="secondary"
                target={"_blank"}
                href={`/goals/${goalId}`}
              />
            </TooltipTrigger>
            <TooltipContent>{t("common:view")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger>
              <ButtonIcon
                icon={<FontAwesomeIcon icon="fa-light fa-xmark" />}
                variant="secondary"
                onClick={onClose}
              />
            </TooltipTrigger>
            <TooltipContent>{t("common:close")}</TooltipContent>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack className="grow overflow-y-auto px-6 border-t-dark/5 pb-11">
        <GoalDetailsContent goalId={goalId} compact />
      </Stack>
      <Stack className="px-4 py-3 border-t border-dark/5 shrink-0">
        <ButtonLink href={`/goals/${goalId}`} size={"lg"} target={"_blank"}>
          {t("goals:view_goal")}
        </ButtonLink>
      </Stack>
    </Stack>
  );
}
